import React, { useState } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Alert,
  Collapse,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { HelpNavigation } from './HelpNavigation';
import { HelpMobilePane } from './HelpMobilePane';
import { MarkdownContent } from './MarkdownContent';
import { LoadingSpinner } from '../shared';
import { useTrainingManual } from '../../hooks/useTrainingManual';

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** Width of the contents sidebar. Referenced by the Collapse and the panel. */
const SIDEBAR_WIDTH = 280;

/**
 * The reading column is this minus SIDEBAR_WIDTH, so it stays a comfortable
 * measure on a large monitor. maxWidth: '100%' on the paper keeps the drawer
 * from overflowing a narrow window.
 */
const DRAWER_WIDTH = { xs: '100%', sm: '90%', md: 780, lg: 920, xl: 1040 };

/**
 * Training manual drawer. Opens from the right with a contents sidebar and a
 * reading pane. All server work lives in useTrainingManual.
 */
export const HelpDrawer: React.FC<HelpDrawerProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const {
    navigation,
    activeDocId,
    content,
    title,
    loading,
    error,
    navLoading,
    navError,
    selectDoc,
    loadDocument,
    clearActiveDoc,
  } = useTrainingManual(open);

  const navPanel = navLoading ? (
    <LoadingSpinner message="Loading contents..." />
  ) : navError ? (
    <Box sx={{ p: 2 }}>
      <Alert severity="error">{navError}</Alert>
    </Box>
  ) : (
    <HelpNavigation items={navigation} activeDocId={activeDocId} onSelectDoc={selectDoc} />
  );

  const readingPane = (
    <MarkdownContent
      content={content}
      loading={loading}
      error={error}
      onNavigateToDoc={loadDocument}
      docs={navigation}
    />
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={400}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : DRAWER_WIDTH,
          maxWidth: '100%',
          '@media print': {
            width: '100%',
            maxWidth: '100%',
            position: 'relative',
            boxShadow: 'none',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          '@media print': {
            height: 'auto',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '@media print': {
              display: 'none',
            },
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {title || 'Training Manual'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isMobile && (
              <Tooltip
                title={sidebarCollapsed ? 'Show the contents list' : 'Hide the contents list'}
              >
                <IconButton
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  size="small"
                  aria-label={
                    sidebarCollapsed ? 'Show the contents list' : 'Hide the contents list'
                  }
                  sx={{ color: 'inherit' }}
                >
                  {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close the training manual"
              sx={{ color: 'inherit' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Main content area */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            '@media print': {
              overflow: 'visible',
              flex: 'none',
            },
          }}
        >
          {isMobile ? (
            /* Contents list fills the drawer until a document is picked */
            <Box
              sx={{
                width: '100%',
                display: navLoading || navError ? 'flex' : 'block',
                flexDirection: 'column',
              }}
            >
              {navPanel}
            </Box>
          ) : (
            <>
              <Collapse
                in={!sidebarCollapsed}
                orientation="horizontal"
                timeout={300}
                sx={{
                  '& .MuiCollapse-wrapper': {
                    height: '100%',
                  },
                  '& .MuiCollapse-wrapperInner': {
                    height: '100%',
                    width: SIDEBAR_WIDTH,
                  },
                  '@media print': {
                    display: 'none',
                  },
                }}
              >
                {/* The divider between the two columns belongs here, not in
                    HelpNavigation: this wrapper is the desktop column, and it
                    is also what the loading and error states render into.
                    HelpNavigation used to draw the same border, giving two. */}
                <Box
                  sx={{
                    width: SIDEBAR_WIDTH,
                    height: '100%',
                    display: navLoading || navError ? 'flex' : 'block',
                    flexDirection: 'column',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                  }}
                >
                  {navPanel}
                </Box>
              </Collapse>

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  backgroundColor: 'background.default',
                  position: 'relative',
                  transition: 'flex 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '@media print': {
                    overflow: 'visible',
                    flex: 'none',
                    position: 'static',
                  },
                }}
              >
                {readingPane}
              </Box>
            </>
          )}
        </Box>

        {isMobile && activeDocId && (
          <HelpMobilePane
            title={title}
            content={content}
            loading={loading}
            error={error}
            docs={navigation}
            onNavigateToDoc={loadDocument}
            onBack={clearActiveDoc}
          />
        )}
      </Box>
    </Drawer>
  );
};
